import { Check, Hash, MessageCircle, MessageSquare } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { closeNotification } from '@/lib/notifications/browser-service';
import { colorFromName, initialsFromName } from '@/components/ui-kit';
import { useChatDockStore } from '../chat-dock-store';
import { useTeamChatLiveAlertStore, type TeamChatLiveAlert } from '../live-alert-store';
import type { Channel } from '../queries';
import { buildTeamChatWidgetAlerts } from '../widget-alerts';
import { alertDisplayCopy,cappedCount } from '../alert-copy';
import { useTranslation } from '@/lib/i18n/react';
import { isDocumentAttended, isTeamChatAttended, serverDocumentAttention, subscribeDocumentAttention } from '../attention-view';

/**
 * Persistent internal-chat preview rendered inside CommsHub's launcher column.
 * Only the newest channel/DM is shown at a time; acknowledging/opening it
 * reveals the next pending conversation. This keeps the launcher compact while
 * the badge still counts every unacknowledged message across all cards.
 */
export function TeamChatLiveAlerts({
  channels = [],
  currentUserId = null,
  totalUnread = 0,
}: {
  channels?: Channel[];
  currentUserId?: string | null;
  totalUnread?: number;
}) {
  const {t}=useTranslation();
  const alerts = useTeamChatLiveAlertStore((state) => state.alerts);
  const dismissedUnreadVersions = useTeamChatLiveAlertStore(
    (state) => state.dismissedUnreadVersions,
  );
  const acknowledge = useTeamChatLiveAlertStore((state) => state.acknowledge);
  const dismissUnread = useTeamChatLiveAlertStore((state) => state.dismissUnread);
  const openChat = useChatDockStore((state) => state.openChat);
  const openChats = useChatDockStore((state) => state.openChats);
  const minimized = useChatDockStore((state) => state.minimized);
  const fullViewChannelId = useTeamChatLiveAlertStore((state) => state.activeFullViewChannelId);
  const documentAttended = useSyncExternalStore(
    subscribeDocumentAttention, isDocumentAttended, serverDocumentAttention,
  );
  const widgetAlerts = buildTeamChatWidgetAlerts(
    alerts,
    channels,
    currentUserId,
    dismissedUnreadVersions,
  ).filter((item) => !isTeamChatAttended(
    item.channelId, { openChats, minimized }, fullViewChannelId, documentAttended,
  ));
  const alert = widgetAlerts[0];

  if (!alert) return null;

  const livePending = alerts.reduce((total, item) => total + item.messageCount, 0);
  const totalPending = Math.max(totalUnread, livePending, alert.messageCount);
  const otherChats = Math.max(0, widgetAlerts.length - 1);

  const dismissCurrent = () => {
    acknowledge(alert.channelId);
    dismissUnread(
      alert.channelId,
      alert.source === 'realtime' ? undefined : alert.unreadVersion,
    );
  };

  return (
    <section
      aria-label={t('comms.pendingAlerts')}
      className="team-chat-live-preview mb-2 max-h-[calc(100dvh-6rem)] w-[21rem] max-w-[calc(100vw_-_2rem)] overflow-y-auto"
    >
      <LiveChatPreview
        alert={alert}
        totalPending={totalPending}
        otherChats={otherChats}
        onAcknowledge={() => {
          closeNotification(alert.channelId);
          dismissCurrent();
        }}
        onOpen={() => {
          closeNotification(alert.channelId);
          dismissCurrent();
          openChat(alert.channelId);
        }}
      />
    </section>
  );
}

function LiveChatPreview({
  alert,
  totalPending,
  otherChats,
  onAcknowledge,
  onOpen,
}: {
  alert: TeamChatLiveAlert;
  totalPending: number;
  otherChats: number;
  onAcknowledge: () => void;
  onOpen: () => void;
}) {
  const {t}=useTranslation();
  const copy=alertDisplayCopy(alert);
  const destination = alert.isDirect ? t('comms.direct') : `#${copy.channel}`;
  const pendingLabel=t('comms.pendingMessages',{count:totalPending});

  return (
    <article
      role="status"
      className="relative overflow-hidden rounded-md border border-blaze-violet/25 text-fg-1 shadow-[0_18px_48px_rgba(30,24,55,0.3)] [background:var(--bg-elev)]"
    >
      <div className="h-1 grad-blaze" />
      <div className="p-3">
        <div className="flex items-start gap-2.5">
          <span
            className="relative grid h-10 w-10 shrink-0 place-items-center rounded-sm text-xs font-extrabold text-white shadow-sm"
            style={{ background: colorFromName(copy.sender) }}
            aria-hidden="true"
          >
            {initialsFromName(copy.sender)}
            <span className="absolute -bottom-1 -end-1 grid h-4 w-4 place-items-center rounded-full bg-blaze-violet text-white ring-2 ring-[var(--bg-elev)]">
              {alert.isDirect ? <MessageCircle size={9} /> : <Hash size={9} />}
            </span>
          </span>

          <div className="min-w-0 flex-1 pe-9">
            <p className="break-words text-xs font-extrabold text-fg-1">{alert.isMention?t('notifications.mentionTitle').split(/(\{name\})/).map((part,index)=>part==='{name}'?<bdi dir="auto" key={index}>{copy.sender}</bdi>:<span key={index}>{part}</span>):<bdi dir="auto">{copy.sender}</bdi>}</p>
            <p className="mt-0.5 break-words text-[9px] font-semibold uppercase tracking-[0.12em] text-blaze-violet">
              {t('comms.internal')} · <bdi dir="auto">{destination}</bdi>
            </p>
          </div>

          <span
            className="absolute end-3 top-3 flex h-6 min-w-6 items-center justify-center rounded-full bg-blaze-orange px-1.5 text-[10px] font-extrabold text-white shadow-md ring-2 ring-[var(--bg-elev)]"
            aria-label={pendingLabel}
            title={pendingLabel}
          >
            {cappedCount(totalPending)}
          </span>
        </div>

        <button
          type="button"
          onClick={onOpen}
          className="mt-3 block min-h-11 w-full rounded-sm border border-border-subtle bg-paper px-3 py-2.5 text-start transition-colors hover:border-blaze-violet/30 hover:bg-blaze-violet/5"
          aria-label={t('comms.openMessage',{name:copy.sender})}
        >
          <span className="line-clamp-2 whitespace-pre-wrap break-words text-[12px] leading-relaxed text-fg-2">
            <bdi dir="auto">{copy.preview}</bdi>
          </span>
          {alert.messageCount > 1 && (
            <span className="mt-1.5 block text-[9px] font-bold text-blaze-orange">
              {t('comms.conversationMessages',{count:alert.messageCount})}
            </span>
          )}
        </button>

        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 break-words text-[9px] text-fg-3">
            {otherChats > 0
              ? t('comms.otherChats',{count:otherChats})
              : t('comms.remainsVisible')}
          </p>
          <div className="flex max-w-full flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={onAcknowledge}
              className="inline-flex min-h-11 min-w-11 items-center gap-1 rounded-xs border border-border-subtle px-2 py-2 text-start text-[11px] font-bold text-fg-2 transition-colors hover:bg-paper hover:text-fg-1"
            >
              <Check size={11} aria-hidden /> {t('comms.acknowledge')}
            </button>
            <button
              type="button"
              onClick={onOpen}
              className="inline-flex min-h-11 min-w-11 items-center gap-1 rounded-xs bg-blaze-violet px-2 py-2 text-start text-[11px] font-bold text-white transition-colors hover:bg-[#6630dc]"
            >
              <MessageSquare size={11} aria-hidden /> {t('comms.openChat')}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
